import { EventModel, EventScoreboard, SetGameScore, Turn } from '@frontend/sports/betting-offer/feature/model';

import { ScoreboardPairGameViewModel } from './pair-game-scoreboard.viewmodel';
import { ScoreboardStat, ScoreboardStatList, SetGameScoreboardStats } from './scoreboard-stats';

export class SetScoreboardViewModel extends ScoreboardPairGameViewModel {
    protected turnKey = 'turn';
    protected pointsKey = 'points';
    protected setsKey = 'sets';

    stats: SetGameScoreboardStats;

    constructor() {
        super();
        this.stats = new SetGameScoreboardStats();
    }

    private getTurn(score: SetGameScore): { player1: number; player2: number } {
        switch (score.currentlyServing) {
            case Turn.Player01:
                return { player1: 1, player2: 0 };
            case Turn.Player02:
                return { player1: 0, player2: 1 };
            default:
                return { player1: 0, player2: 0 };
        }
    }

    private getPoints(score: SetGameScore): { player1: string; player2: string } | null {
        if (!score.participant1 || !score.participant2) {
            return null;
        }

        return { player1: score.participant1.points || '', player2: score.participant2.points || '' };
    }

    private getSets(score: SetGameScore): { player1: number; player2: number }[] {
        if (!score.participant1 || !score.participant2) {
            return [];
        }
        const player1Sets = score.participant1.sets;
        const player2Sets = score.participant2.sets;

        const result: { player1: number; player2: number }[] = [];

        for (let i = 0; i < player1Sets.length; i++) {
            const player1 = this.parsePoints(player1Sets[i].points);
            const player2 = this.parsePoints(player2Sets[i].points);

            result.push({ player1, player2 });
        }

        return result;
    }

    protected createStats(): SetGameScoreboardStats {
        return new SetGameScoreboardStats();
    }

    protected fillStats(stats: SetGameScoreboardStats, scoreboard: EventScoreboard): void {
        const score = scoreboard.getScore<SetGameScore>();
        if (!score) {
            return;
        }

        stats.isActive = scoreboard.started && score.currentlyServing !== Turn.None;

        const turn = this.getTurn(score);
        stats.turn = new ScoreboardStat(this.turnKey, turn.player1, turn.player2, 0, stats.isActive && !scoreboard.isFinished, '');

        const points = this.getPoints(score);
        if (points) {
            stats.points = new ScoreboardStat(this.pointsKey, points.player1, points.player2, 1, stats.isActive, '');
        }
        const sets = this.getSets(score);
        for (let i = 0; i < sets.length; i++) {
            const set = sets[i];
            stats.sets.addStat(new ScoreboardStat(`${this.setsKey}_${i}`, set.player1, set.player2, i, true, ''));
        }

        this.fillEmptySets(stats, score);

        stats.sportId = this.stats.sportId;
    }

    protected fillEmptySets(stats: SetGameScoreboardStats, score: SetGameScore): void {}

    protected updateStats(scoreboard: EventScoreboard): void {
        const stats = this.createStats();
        this.fillStats(stats, scoreboard);
        this.stats = stats;
    }

    override fillFromEvent(event: EventModel): void {
        super.fillFromEvent(event);
        if (event) {
            this.stats.sportId = event.sport.id;
        }
    }

    override fillFromScoreboard(scoreboard: EventScoreboard): void {
        super.fillFromScoreboard(scoreboard);

        this.updateStats(scoreboard);
    }

    override updateFromScoreboard(scoreboard: EventScoreboard): void {
        super.updateFromScoreboard(scoreboard);

        this.updateStats(scoreboard);
    }

    get turn(): ScoreboardStat {
        return this.stats.turn;
    }

    get points(): ScoreboardStat | null {
        return this.stats.points;
    }

    get sets(): ScoreboardStatList {
        return this.stats.sets;
    }

    private parsePoints(val: string | number): number {
        if (typeof val === 'string') {
            const result = parseInt(val);

            return isNaN(result) ? -1 : result;
        }

        return val;
    }
}

export class TennisScoreboardViewModel extends SetScoreboardViewModel {
    arePointsAreVisible: boolean;

    protected override fillStats(stats: SetGameScoreboardStats, scoreboard: EventScoreboard): void {
        super.fillStats(stats, scoreboard);
        if (stats.points) {
            stats.points.isVisible = this.arePointsAreVisible && stats.isActive;
        }
    }
}
